import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useCartContext } from "../../components/providers/CartProvider.jsx";
import { ShoppingCart } from 'lucide-react';
import getMediaUrl, { getDefaultProductThumbnail } from '../../components/tools/utils.jsx';

const fmt = (n) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

const ProductImage = ({ product }) => {
    const src = getDefaultProductThumbnail(product);
    if (!src) return (
        <div className="h-48 w-full flex items-center justify-center bg-gray-200 dark:bg-base-200 text-gray-400 text-sm">
            Sin imagen
        </div>
    );
    return (
        <img
            src={getMediaUrl(src)}
            alt={product?.name}
            loading="lazy"
            decoding="async"
            className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
    );
};

const CardProduct = ({ product, isAdmin, onEdit, onDelete }) => {
    const navigate = useNavigate();
    const { addToCart } = useCartContext();
    const [adding, setAdding] = useState(false);

    const handleAddToCart = async (e) => {
        e.stopPropagation();
        if (adding) return;
        setAdding(true);
        await addToCart(product);
        setTimeout(() => setAdding(false), 1800);
    };

    const handleViewProduct = () => navigate(`/product/${product.id}`);

    // Always show a fixed short description — no height change on hover
    const shortDesc = (() => {
        if (!product?.short_description) return '';
        const words = product.short_description.split(' ');
        return words.length <= 8 ? product.short_description : words.slice(0, 8).join(' ') + '…';
    })();

    return (
        <div
            className="relative rounded-xl overflow-hidden cursor-pointer group
                       bg-white dark:bg-base-300 shadow-sm
                       border border-black/5 dark:border-white/5
                       hover:shadow-lg hover:border-black/10 dark:hover:border-white/10
                       transition-all duration-300"
            onClick={handleViewProduct}
        >
            {/* Image */}
            <div className="relative overflow-hidden">
                <ProductImage product={product} />

                {/* Add to cart button — overlay top-right */}
                {!isAdmin && (
                    <button
                        onClick={handleAddToCart}
                        className={`absolute top-2 right-2 z-10
                            w-9 h-9 rounded-full flex items-center justify-center
                            transition-all duration-200 shadow-md
                            ${adding
                                ? 'bg-green-500 text-white scale-110'
                                : 'bg-white/90 dark:bg-base-300/90 text-gray-700 dark:text-gray-200 hover:bg-brand-red hover:text-white'
                            }`}
                    >
                        <ShoppingCart size={15} />
                    </button>
                )}
            </div>

            {/* Info */}
            <div className="p-3 flex flex-col gap-1">
                <h2 className="font-semibold text-sm md:text-base leading-snug dark:text-white text-gray-900 line-clamp-2">
                    {product?.name}
                </h2>
                {shortDesc && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                        {shortDesc}
                    </p>
                )}
                <p className="text-sm font-bold mt-1 dark:text-white text-gray-900">
                    {fmt(product?.price)}
                </p>
            </div>

            {/* Admin actions */}
            {isAdmin && (
                <div className="flex gap-2 px-3 pb-3">
                    <button
                        className="btn btn-primary btn-xs flex-1"
                        onClick={(e) => { e.stopPropagation(); onEdit(product?.id); }}
                    >
                        Editar
                    </button>
                    <button
                        className="btn btn-error btn-xs flex-1"
                        onClick={(e) => { e.stopPropagation(); onDelete(product?.id); }}
                    >
                        Borrar
                    </button>
                </div>
            )}
        </div>
    );
};

export default CardProduct;
